import { useState } from 'react'
import { Alert, Form, Input, Radio, Select, type RadioChangeEvent } from 'antd'
import { useTranslation } from 'react-i18next'

import type { CarrierRoot, ShipFulfillmentOrderResponse, ShippingOptionRoot } from '@repo/sdk'

import { useCarrierOptions } from './carrierConfig'
import { TrackingValidationPanel } from './TrackingValidationPanel'
import { resolveValidationGate, useTrackingValidation } from './useTrackingValidation'
import { useShipOrder } from './useShipOrder'
import { Popup, confirmPopup } from '@/components/popup'

interface ShipOrderModalProps {
  fulfillmentOrderId: string
  /** How many fulfillment orders this shipment covers (combined shipping ships them all). */
  targetCount?: number
  open: boolean
  onClose: () => void
  /** Called after a successful shipment (views already refreshed). */
  onShipped?: (result: ShipFulfillmentOrderResponse) => void
}

interface ShipFormValues {
  shippingOption: ShippingOptionRoot
  carrier?: CarrierRoot
  trackingNumber?: string
}

export function ShipOrderModal({
  fulfillmentOrderId,
  targetCount = 1,
  open,
  onClose,
  onShipped,
}: ShipOrderModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<ShipFormValues>()
  const carrierOptions = useCarrierOptions()
  const { shipOrder, isShipping } = useShipOrder({ onShipped })
  const [shippingOption, setShippingOption] = useState<ShippingOptionRoot>('STANDARD')
  const { validationResult, resetValidation, validate, isValidating, statusMessage } =
    useTrackingValidation()

  // The modal stays open (OK button loading) until the server answered, and closes only on
  // success. It used to close at once: a failed shipment lost the entered tracking number, and the
  // dealer could reopen it and submit again while the first request was still running.
  const submitShipping = (values: ShipFormValues) => {
    shipOrder(
      {
        fulfillmentOrderId,
        shippingOption: values.shippingOption,
        carrier: values.shippingOption === 'STANDARD' ? values.carrier : null,
        trackingNumber: values.shippingOption === 'STANDARD' ? values.trackingNumber?.trim() : null,
      },
      {
        onSuccess: () => {
          form.resetFields()
          resetValidation()
          onClose()
        },
      },
    )
  }

  /** Normal path: plain "proceed?" confirmation. */
  const confirmShipping = (values: ShipFormValues) => {
    confirmPopup({
      title: t('common.modals.confirmShippingTitle'),
      content: t('common.modals.confirmShippingMessage'),
      okButtonProps: { 'data-testid': 'ship-order-confirm-ok-btn' },
      cancelButtonProps: { 'data-testid': 'ship-order-confirm-cancel-btn' },
      onOk: () => submitShipping(values),
    })
  }

  /** Validation did not pass cleanly: show the reason and ask whether to proceed anyway. */
  const confirmShippingAnyway = (values: ShipFormValues, warning: string) => {
    confirmPopup({
      title: t('common.modals.confirmShippingTitle'),
      content: (
        <>
          <p>{warning}</p>
          <p>{t('pages.orders.trackingValidation.proceedAnywayShip')}</p>
        </>
      ),
      okText: t('pages.orders.trackingValidation.proceedAnywayButton'),
      okButtonProps: { 'data-testid': 'ship-order-confirm-ok-btn' },
      cancelButtonProps: { 'data-testid': 'ship-order-confirm-cancel-btn' },
      onOk: () => submitShipping(values),
    })
  }

  const handleShippingOptionChange = (e: RadioChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Radio value is always a valid ShippingOptionRoot
    setShippingOption(e.target.value as ShippingOptionRoot)
    resetValidation()
  }

  const handleOk = () => {
    void form.validateFields().then(async (values) => {
      if (values.shippingOption !== 'STANDARD' || !values.carrier || !values.trackingNumber) {
        confirmShipping(values)
        return
      }

      const outcome = await validate(values.carrier, values.trackingNumber)
      const gate = resolveValidationGate(
        outcome,
        statusMessage,
        t('pages.orders.trackingValidation.requestFailedProceed'),
      )
      if (gate.action === 'block') return
      if (gate.action === 'confirm') {
        confirmShippingAnyway(values, gate.warning)
        return
      }
      confirmShipping(values)
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setShippingOption('STANDARD')
    resetValidation()
    onClose()
  }

  const handleValidateOnly = () => {
    void form.validateFields(['carrier', 'trackingNumber']).then(async () => {
      const values = form.getFieldsValue()
      if (!values.carrier || !values.trackingNumber) return
      await validate(values.carrier, values.trackingNumber)
    })
  }

  return (
    <Popup
      data-testid="ship-order-modal"
      title={t('common.buttons.ship')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isShipping || isValidating}
      okText={t('common.buttons.confirm')}
      cancelText={t('common.buttons.cancel')}
      okButtonProps={{ 'data-testid': 'ship-order-modal-ok-btn' }}
      cancelButtonProps={{ 'data-testid': 'ship-order-modal-cancel-btn' }}
    >
      {targetCount > 1 && (
        <Alert
          data-testid="ship-order-group-notice"
          type="warning"
          showIcon
          message={t('pages.orders.groupShipNotice', { count: targetCount })}
          style={{ marginBottom: 16 }}
        />
      )}
      <Alert
        type="info"
        showIcon
        message={t('common.shipping.shippingFeeNonRefundNotice')}
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ shippingOption: 'STANDARD' }}
        onValuesChange={(changed) => {
          if ('carrier' in changed || 'trackingNumber' in changed) resetValidation()
        }}
      >
        <Form.Item
          name="shippingOption"
          label={t('common.shipping.shippingOption')}
          rules={[{ required: true }]}
        >
          <Radio.Group data-testid="ship-order-radio-group" onChange={handleShippingOptionChange}>
            <Radio data-testid="ship-order-radio-standard" value="STANDARD">
              {t('common.shipping.standardShipping')}
            </Radio>
            <Radio data-testid="ship-order-radio-quick-direct" value="QUICK_DIRECT">
              {t('common.shipping.quickDirectDelivery')}
            </Radio>
          </Radio.Group>
        </Form.Item>

        {shippingOption === 'STANDARD' && (
          <>
            <Form.Item
              name="carrier"
              label={t('common.shipping.courierCompany')}
              rules={[{ required: true }]}
            >
              <Select
                data-testid="ship-order-carrier-select"
                placeholder={t('common.modals.selectCarrier')}
                options={carrierOptions}
              />
            </Form.Item>
            <Form.Item
              name="trackingNumber"
              label={t('common.shipping.trackingNumber')}
              rules={[{ required: true }]}
            >
              <Input
                data-testid="ship-order-tracking-input"
                placeholder={t('common.modals.enterTrackingNumber')}
              />
            </Form.Item>
            <TrackingValidationPanel
              result={validationResult}
              isValidating={isValidating}
              onValidate={handleValidateOnly}
              testIdPrefix="ship-order"
            />
          </>
        )}
      </Form>
    </Popup>
  )
}
